import { supabase } from '../lib/supabase';

/**
 * One row of operations.operator_messages — the preset warnings/instructions an
 * operator can send to the visitor in the headset ("Niet rennen"). The agent
 * plays the pre-rendered clip {key}_{lang}_v{clip_version}.wav from the
 * operator-messages bucket; adding a message is one DB row + two clip uploads,
 * no release. See RealityAgent/docs/visitor-message-contract.md.
 */
export interface OperatorMessage {
  key: string;
  label: string;
  text_nl: string;
  text_en: string;
  clip_version: number;
  sort: number;
  active: boolean;
}

/**
 * Hardcoded fallback matching the migration seed, so the feature keeps working
 * before the operator_messages migration is applied (or if the query fails).
 */
const DEFAULT_MESSAGES: OperatorMessage[] = [
  { key: 'no_running', label: 'Niet rennen', text_nl: 'Niet rennen alsjeblieft.', text_en: "Please don't run.", clip_version: 1, sort: 10, active: true },
  { key: 'no_screaming', label: 'Niet schreeuwen', text_nl: 'Niet schreeuwen alsjeblieft.', text_en: "Please don't scream.", clip_version: 1, sort: 20, active: true },
  { key: 'stay_in_area', label: 'Blijf in speelgebied', text_nl: 'Blijf binnen het speelgebied alsjeblieft.', text_en: 'Please stay inside the play area.', clip_version: 1, sort: 30, active: true },
  { key: 'staff_incoming', label: 'Medewerker komt eraan', text_nl: 'Er komt een medewerker naar je toe.', text_en: 'A staff member is coming to help you.', clip_version: 1, sort: 40, active: true },
];

/**
 * Fetch-once cache (same pattern as issueCategories). Only successful,
 * non-empty results are cached so a pre-migration deploy retries next time.
 */
let messagesCache: OperatorMessage[] | null = null;

export async function getOperatorMessages(): Promise<OperatorMessage[]> {
  if (messagesCache) {
    return messagesCache;
  }

  try {
    const { data, error } = await supabase
      .schema('operations')
      .from('operator_messages')
      .select('key, label, text_nl, text_en, clip_version, sort, active')
      .eq('active', true)
      .order('sort', { ascending: true });

    if (error || !data || data.length === 0) {
      if (error) {
        console.error('Error fetching operator messages (using defaults):', error);
      }
      return DEFAULT_MESSAGES;
    }

    messagesCache = data as OperatorMessage[];
    return messagesCache;
  } catch (err) {
    console.error('Exception fetching operator messages (using defaults):', err);
    return DEFAULT_MESSAGES;
  }
}

/** Clear the cache (e.g. after an admin edits messages). */
export function clearOperatorMessagesCache(): void {
  messagesCache = null;
}
